<script setup lang="ts">
import { LockOutlined, UserOutlined } from '@ant-design/icons-vue';


import { useNotificationsStore } from '@/stores/notifications';
import { useAccountsStore } from '@/stores/accounts';

import { retrieveErrorMsg } from '@/helpers/retrieveErrorMsg';
import Onboard from '@/pages/excel-addin/Onboard.vue';
import { reactive } from 'vue';
import { analyticsTrack, EventNames } from '@/plugins/usage-analytics';
import { ROUTE_NAMES } from '@/router/route-names';
import { useRoute, useRouter } from 'vue-router';

const notificationsStore = useNotificationsStore();
const accountsStore = useAccountsStore();

const route = useRoute();
const router = useRouter();

const state = reactive({
  loading: {
    loggingIn: false,
  },
  showOnboard: true,
  form: {
    username: '',
    password: '',
  },
});

function closeOnboard() {
  console.log('run')
  state.showOnboard = false
}



async function signIn() {
  try {
    state.loading.loggingIn = true;
    await accountsStore.loginCommercial({
      username: state.form.username,
      password: state.form.password,
    });
    state.loading.loggingIn = false;
    analyticsTrack(EventNames.LOGIN_SUCCESS, {});

    // postMessageToParent('checkLoggedInAndFetchAuthPrice', null);

    const toQuery = route.query.to;
    if (toQuery && typeof toQuery === 'string') {
      // notificationsStore.pushClientNotification({
      //   message: 'Redirecting back to ' + toQuery,
      //   level: 'info',
      // });
      router.push({ name: toQuery });
    } else {
      router.push({ name: ROUTE_NAMES.HOME });
    }
  } catch (err: any) {
    state.loading.loggingIn = false;
    analyticsTrack(EventNames.LOGIN_FAIL, { message: retrieveErrorMsg(err) });
    notificationsStore.pushClientNotification({
      message: retrieveErrorMsg(err),
      level: 'error',
    });
    console.error(err);
  }
}
</script>

<template>
  <div class="w-full h-full">
    <Transition name="onboard-swap" mode="out-in">
      <Onboard v-if="state.showOnboard" key="onboard" @login="closeOnboard" />
      <div
        v-else
        key="login"
        class="flex min-h-full flex-col justify-start px-4 py-6 sm:px-6"
      >
        <div class="mx-auto w-full max-w-md">
          <div class="px-1">


            <div class="mb-10">
              <h2 class="mt-2 text-3xl font-bold tracking-tight text-gray-900">
                Welcome back
              </h2>
              <p class="mt-2 text-base text-gray-600">
                Enter your credentials to use Spark in Excel.
              </p>
            </div>

            <form @submit.prevent="signIn" class="space-y-6">
              <div>
                <label class="text-sm font-semibold text-gray-700"
                  >Work Email</label
                >
                <a-input
                  v-model:value="state.form.username"
                  size="large"
                  placeholder="name@company.com"
                  type="email"
                  autocomplete="username"
                  required
                  class="custom-input"
                >
                  <template #prefix>
                    <UserOutlined class="text-gray-400 transition-colors" />
                  </template>
                </a-input>
              </div>

              <div>
                <label class="text-sm font-semibold text-gray-700"
                  >Password</label
                >
                <a-input-password
                  v-model:value="state.form.password"
                  size="large"
                  placeholder="••••••••••••"
                  autocomplete="current-password"
                  required
                  class="custom-input"
                >
                  <template #prefix>
                    <LockOutlined class="text-gray-400 transition-colors" />
                  </template>
                </a-input-password>
              </div>

              <div class="flex items-center justify-end">
                <a
                  href="https://app.sparkcommodities.com/password-reset/"
                  target="_blank"
                  @click="analyticsTrack(EventNames.LOGIN_FORGOT_PASSWORD_CLICK, {})"
                >
                  <SparkButton
                    look="text"
                    color="green"
                    class="px-0 text-sm font-medium hover:underline"
                  >
                    Forgot password?
                  </SparkButton>
                </a>
              </div>

              <div>
                <SparkButton
                  class="w-full"
                  size="lg"
                  type="submit"
                  :loading="state.loading.loggingIn"
                >
                  <span class="text-base font-bold">Sign in</span>
                </SparkButton>
              </div>
            </form>

            <div class="mt-8">
              <div class="relative">
                <div class="absolute inset-0 flex items-center">
                  <div class="w-full border-t border-gray-200"></div>
                </div>
                <div class="relative flex justify-center text-sm">
                  <span class="bg-shade-light px-4 text-gray-500">
                    Don't have an account?
                  </span>
                </div>
              </div>

              <div class="mt-6">
                <a
                  href="https://app.sparkcommodities.com/signup/?referer=kpler-terminal"
                  target="_blank"
                  @click="analyticsTrack(EventNames.LOGIN_CREATE_ACCOUNT_CLICK, {})"
                >
                  <SparkButton
                    class="w-full border-2 border-gray-200 bg-white font-semibold text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                    size="lg"
                    look="outlined"
                    color="gray"
                  >
                    Create new account
                  </SparkButton>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>

</template>
<style lang="scss" scoped>
/* we will explain what these classes do next! */
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.5s ease;
}

.collapse-leave-to {
  height: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Onboard -> login: the login page fades in AFTER the onboard view has left
   (mode="out-in"). The onboard exit choreography lives in Onboard.vue.
   Opacity-only — a transform on this min-h-full container would extend the
   scroll height and flash a temporary scrollbar. */
.onboard-swap-enter-active {
  transition: opacity 0.4s ease;
}
.onboard-swap-enter-from {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .onboard-swap-enter-active {
    transition: opacity 0.2s ease;
  }
}
</style>